import { useCallback, useEffect, useState } from "react";
import Button from "@/components/Button";
import SectionHeading from "@/components/SectionHeading";
import { downloadQRCode, createQRCode } from "@/components/QrComponents/utils/qrCodeGenerator";
import { getTld } from "@/lib/urlUtils";

interface SelfCheckInSectionProps {
  activityId: string;
  activityTitle?: string;
}

export default function SelfCheckInSection({ activityId, activityTitle }: SelfCheckInSectionProps) {
  const [qrCodeImage, setQrCodeImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const checkInUrl = `https://${getTld()}/member/activities/${activityId}/check-in`;

  const generateQRCode = useCallback(async () => {
    setLoading(true);
    try {
      const canvas = await createQRCode(checkInUrl);
      const imageUrl = canvas.toDataURL("image/png");
      setQrCodeImage(imageUrl);
    } catch (error) {
      console.error("Error generating QR code:", error);
    } finally {
      setLoading(false);
    }
  }, [checkInUrl]);

  useEffect(() => {
    generateQRCode();
  }, [generateQRCode]);

  const handleDownloadQRCode = async () => {
    const filename = activityTitle
      ? `${activityTitle.replace(/[^a-zA-Z0-9]/g, "-")}-check-in-qr`
      : "activity-check-in-qr";
    await downloadQRCode(checkInUrl, filename);
  };

  const handleViewCheckInPage = () => {
    window.open(checkInUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="border-t border-sys-brand-outline-variant pt-12">
      <div className="flex items-center justify-between mb-1">
        <SectionHeading heading="Self Check-In Attendance" />
        <div className="flex gap-2">
          <Button variant="outlined" onClick={handleViewCheckInPage} icon="open_in_new">
            View Check-In Page
          </Button>
          <Button variant="outlined" onClick={handleDownloadQRCode} icon="download">
            Download QR Code
          </Button>
        </div>
      </div>
      <p className="text-body-medium text-sys-brand-on-surface-variant mb-6">
        Downloadable quick link to the activity self check-in page.
      </p>
      <div className="flex">
        {loading ? (
          <div className="w-[200px] h-[200px] flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : qrCodeImage ? (
          <img src={qrCodeImage} alt="Self Check-In QR Code" className="w-[200px] h-[200px]" />
        ) : (
          <div className="w-[200px] h-[200px] flex items-center justify-center bg-gray-100 text-gray-500">
            Failed to load QR code
          </div>
        )}
      </div>
    </div>
  );
}
